import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Clock, Brain } from "lucide-react";
import { api, API_BASE } from "./api_service/api";

interface VideoResultModalProps {
  result: any;
  onClose: () => void;
}

const VideoResultModal: React.FC<VideoResultModalProps> = ({
  result,
  onClose,
}) => {
  const [explanation, setExplanation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReal = result.prediction === "Real";
  const aiPercent = result.ai_percentage;
  const humanPercent = result.human_percentage;
  const confidencePercent = result.confidence;
  const duration = result.duration;

  useEffect(() => {
    if (!result?.explanation) return;

    let abortController = new AbortController();

    const startReasoning = async () => {
      setIsLoading(true);
      setError(null);
      setExplanation("");

      try {
        if (!api.isAuthenticated) {
          await api.login();
        }

        const apiKey = (api as any).apiKey;
        const token = (api as any).token;

        if (!apiKey || !token) {
          throw new Error("Authentication failed");
        }

        console.log("🚀 Starting stream...");

        // Process base64_frames: strip data URL prefixes if present
        const processedBase64Frames = (
          result.explanation.base64_frames || []
        ).map((frame: string) => {
          if (typeof frame === "string" && frame.startsWith("data:image")) {
            const commaIndex = frame.indexOf(",");
            return commaIndex !== -1 ? frame.slice(commaIndex + 1) : frame;
          }
          return frame;
        });

        const payload = {
          ...result.explanation,
          base64_frames: processedBase64Frames,
        };

        const response = await fetch(`${API_BASE}/reasoning/stream-video`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": apiKey,
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        console.log("✅ Stream started");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log("✅ Stream complete");
            setIsLoading(false);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim() || !line.startsWith("data: ")) continue;

            try {
              const data = JSON.parse(line.substring(6));
              console.log("📨 Received:", data.type);

              if (data.type === "start") {
                setExplanation(data.content || "");
              } else if (data.type === "chunk") {
                setExplanation((prev) => prev + (data.content || ""));
              } else if (data.type === "done") {
                setIsLoading(false);
                return;
              } else if (data.type === "error") {
                setError(data.content || "An error occurred");
                setIsLoading(false);
                return;
              }
            } catch (e) {
              console.error("Parse error:", e, line);
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.log("Stream aborted");
          return;
        }
        console.error("❌ Error:", err);
        setError(err.message || "Failed to generate reasoning");
        setIsLoading(false);
      }
    };

    startReasoning();

    return () => {
      console.log("🔌 Aborting stream");
      abortController.abort();
    };
  }, [result?.explanation]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div
          className={`p-8 text-center ${
            isReal
              ? "bg-gradient-to-br from-emerald-500 to-teal-600"
              : "bg-gradient-to-br from-rose-500 to-pink-600"
          } text-white`}
        >
          <div className="flex justify-center mb-4">
            {isReal ? (
              <CheckCircle size={80} className="drop-shadow-lg" />
            ) : (
              <AlertCircle size={80} className="drop-shadow-lg" />
            )}
          </div>
          <h2 className="text-4xl font-bold">{result.prediction}</h2>
          <p className="text-white/90 mt-2 text-lg">
            Video Authenticity Analysis
          </p>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6 bg-gray-50">
          {/* AI vs Human Bar */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm font-medium text-gray-600">
              <span>AI-Generated</span>
              <span>Human-Created</span>
            </div>
            <div className="relative h-12 bg-gray-200 rounded-full overflow-hidden shadow-inner">
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-1000"
                style={{ width: `${aiPercent}%` }}
              />
              <div
                className="absolute right-0 top-0 h-full bg-gradient-to-l from-emerald-500 to-teal-500 transition-all duration-1000"
                style={{ width: `${humanPercent}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-center font-bold text-white text-xl drop-shadow">
                {aiPercent}% AI • {humanPercent}% Real
              </div>
            </div>
          </div>

          {/* Confidence & Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 text-center border border-gray-200">
              <div className="text-gray-500 text-sm font-medium flex items-center justify-center gap-2">
                <CheckCircle size={18} className="text-indigo-600" />
                Confidence
              </div>
              <div className="text-3xl font-bold text-indigo-700 mt-2">
                {confidencePercent}%
              </div>
            </div>
            <div className="bg-white rounded-2xl p-5 text-center border border-gray-200">
              <div className="text-gray-500 text-sm font-medium flex items-center justify-center gap-2">
                <Clock size={18} className="text-purple-600" />
                Duration
              </div>
              <div className="text-3xl font-bold text-purple-700 mt-2">
                {duration}s
              </div>
            </div>
          </div>

          {/* Detailed Reasoning */}
          <div className="p-6 rounded-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="w-6 h-6 text-indigo-600" />
              <h4 className="text-lg font-semibold">Detailed Reasoning</h4>
              {isLoading && (
                <div className="ml-auto">
                  <div className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            <div className="min-h-[100px]">
              {error ? (
                <span className="text-red-600">{error}</span>
              ) : explanation ? (
                <pre className="whitespace-pre-wrap font-sans text-gray-700">
                  {explanation}
                </pre>
              ) : (
                <span className="text-gray-500 italic">
                  {isLoading
                    ? "Connecting and generating reasoning..."
                    : "No reasoning available"}
                </span>
              )}
            </div>
          </div>

          {/* Analyzed At */}
          <div className="text-center text-sm text-gray-500">
            Analyzed on{" "}
            {new Date(result.analysed_at).toLocaleString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur transition"
        >
          <X size={24} className="text-white" />
        </button>
      </div>
    </div>
  );
};

export default VideoResultModal;
